import {skipToken} from '@reduxjs/toolkit/query';
import {Card, FormControl, openConfirmDialog} from 'platform/components';
import {Box, HStack, Link, Text, VStack} from 'platform/foundation';

import {useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';

import {useGetBranchV2Query} from '@dms/api/base';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';

import {RequiredTestIdProps, composePath, suffixTestId} from 'shared/web';

import {DAY_LABEL_WIDTH} from '../constants/DAY_LABEL_WIDTH';
import {TIME_INPUT_WIDTH} from '../constants/TIME_INPUT_WIDTH';
import {
  BUSINESS_HOURS_DAYS,
  TelephonyLineFormValues,
  getBusinessHoursFormValues,
  hasAnyBusinessHours,
  hasAnyOpeningHours,
} from '../utils/telephonyLineForm';
import {TelephonyLineBusinessHoursDayRow} from './TelephonyLineBusinessHoursDayRow';

interface TelephonyLineBusinessHoursCardProps extends RequiredTestIdProps {
  control: FormControl<TelephonyLineFormValues>;
  formApi: UseFormReturn<TelephonyLineFormValues>;
  isReadOnly: boolean;
}

export function TelephonyLineBusinessHoursCard(props: TelephonyLineBusinessHoursCardProps) {
  const branchId = useWatch({control: props.control, name: 'branchId'});

  // `currentData`, not `data`: the latter keeps the previously selected branch's response while the
  // newly selected one loads and after it fails, which would copy another branch's hours onto the line.
  const {currentData: branch, isFetching: isBranchFetching} = useGetBranchV2Query(
    isNotNil(branchId) ? {id: branchId} : skipToken
  );

  // Mounted slot inputs do not pick up a whole-array `setValue`, so the grid is remounted after
  // a copy and re-reads the fresh values on mount.
  const [gridGeneration, setGridGeneration] = useState(0);

  const branchSalesHours = branch?.salesOpeningHours;
  const canCopyFromBranch =
    !props.isReadOnly && isNotNil(branchId) && hasAnyOpeningHours(branchSalesHours);

  const applyBranchHours = () => {
    if (isNil(branchSalesHours)) {
      return;
    }

    props.formApi.setValue('businessHours', getBusinessHoursFormValues(branchSalesHours), {
      shouldDirty: true,
    });
    setGridGeneration((generation) => generation + 1);
  };

  const handleCopyFromBranch = () => {
    if (hasAnyBusinessHours(props.formApi.getValues('businessHours'))) {
      openConfirmDialog({
        text: i18n.t('page.settings.telephony.copyHoursFromBranchConfirm'),
        onConfirm: applyBranchHours,
        'data-testid': suffixTestId('copyFromBranch', props),
      });
      return;
    }

    applyBranchHours();
  };

  return (
    <Card
      title={i18n.t('page.settings.telephony.hoursCardTitle')}
      actions={[
        {
          type: 'button',
          variant: 'link',
          leftIcon: 'content/copy',
          title: i18n.t('page.settings.telephony.copyHoursFromBranch'),
          onClick: handleCopyFromBranch,
          isDisabled: !canCopyFromBranch,
          isLoading: isBranchFetching,
          'data-testid': suffixTestId('copyFromBranch', props),
        },
      ]}
      data-testid={suffixTestId('card', props)}
    >
      <VStack spacing={4}>
        <HStack spacing={2} align="center" wrap>
          <Text color="secondary" size="small">
            {i18n.t('page.settings.telephony.hoursHolidaysNote')}
          </Text>
          <Link
            size="small"
            href={composePath(settingsRoutes.holidayCalendar)}
            title={i18n.t('page.settings.telephony.holidayCalendarLink')}
            data-testid={suffixTestId('holidayCalendarLink', props)}
          />
        </HStack>
        <VStack spacing={3}>
          <HStack spacing={4}>
            <Box width={DAY_LABEL_WIDTH} />
            <Box width={TIME_INPUT_WIDTH}>
              <Text size="small" alternative>
                {i18n.t('page.settings.telephony.hoursFrom')}
              </Text>
            </Box>
            <Box width={TIME_INPUT_WIDTH}>
              <Text size="small" alternative>
                {i18n.t('page.settings.telephony.hoursTo')}
              </Text>
            </Box>
          </HStack>
          <VStack key={gridGeneration} spacing={3}>
            {BUSINESS_HOURS_DAYS.map((day) => (
              <TelephonyLineBusinessHoursDayRow
                key={day}
                day={day}
                control={props.control}
                formApi={props.formApi}
                isReadOnly={props.isReadOnly}
                data-testid={suffixTestId('day', props)}
              />
            ))}
          </VStack>
        </VStack>
      </VStack>
    </Card>
  );
}
